'use client';

import heroBackground from '@/public/home/heroBackground.png';
import Image from 'next/image';
import { useRef } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import TrustChip from '@/components/global/TrustChip';
import { CTAButton } from '@/shadcn/ui/button';
import ChipText from '@/components/global/ChipText';
import heroCard from '@/public/home/heroCard.png';
import manInTheOffice from '@/public/home/challengeImage.png';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import AutoScroll from 'embla-carousel-auto-scroll';

interface IServicesProps {
  chipText: string;
  title: PortableTextBlock[];
  services: {
    icon: GROQImage;
    service: string;
  }[];
}

export default function Services({
  chipText,
  title,
  services,
}: IServicesProps) {
  const isMobile = useIsMobile();

  return (
    <section className="w-full flex flex-col items-center pb-[10rem] lg:pb-[17.2rem]">
      <div className="container flex flex-col gap-[7rem] lg:gap-[9rem] items-center justify-center w-full relative overflow-visible">
        <div className="flex flex-col gap-[2rem] w-full items-center">
          {chipText &&
            <ChipText text={chipText} color="purple" />
          }
          <div className="w-full items-center richtext richtext-center prose-headings:text-[4rem] lg:prose-headings:text-[6rem] prose-headings:mb-0">
            <PortableText value={title} components={richTextComponent} />
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-6 justify-between lg:justify-normal w-full border border-[#072922]/[24%]">
          {services?.length > 0 && services?.map((service, index) => {
            const pattern = [2, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 2, 1, 1, 1, 1, 1, 1];
            const patternIndex = index % pattern.length;
            const colSpan = pattern[patternIndex] === 2 ? 'col-span-2' : 'col-span-1';

            return (
              <div key={index} className={`${colSpan} flex flex-col items-center justify-center w-full min-h-[21.875rem] border border-[#072922]/[24%] hover:bg-[#072922] transition-all duration-300 ease-out group relative overflow-hidden py-[5rem]`}>
                <div className="flex flex-col items-center gap-[1rem] absolute top-[5rem] group-hover:top-1/2 group-hover:-translate-y-1/2 transition-all duration-300 ease-out px-[2.9rem]">
                  <Image
                    src={service.icon.src}
                    width={service.icon.width}
                    height={service.icon.height}
                    quality={100}
                    className="w-[4rem] h-[4rem] opacity-[64%] group-hover:brightness-[5] group-hover:contrast-[40] group-hover:invert group-hover:opacity-100 transition-all duration-300 ease-out"
                    alt=""
                  />
                  <p className="text-[3rem] text-center font-[400] font-inter-tight leading-[120%] text-[#072922]/[64%] group-hover:text-green transition-all duration-300 ease-out mb-0 select-none">{service.service}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
